import { Stack, Typography } from '@mui/material';
import { VyIcon } from '@vy/ui-design-system';
import { isEmptyArray } from '@vy/utils';
import React from 'react';
import { VyDialog } from '../../../../components/new-design/feedback/VyDialog';
import { useShareCreditNotes } from '../../../../hooks/requests/use-share-credit-notes';
import { useDialog } from '../../../../hooks/use-dialog';
import { runMutation } from '../../../../utils/vy-mutation';

type ShareCreditNoteEmailDialogProps = {
  creditLedgerEntryId: string;
  customerName: string;
  contactEmails: string[];
  children: React.ReactElement;
};

export const ShareCreditNoteEmailDialog: React.FC<ShareCreditNoteEmailDialogProps> = ({
  creditLedgerEntryId,
  customerName,
  contactEmails,
  children,
}) => {
  const { isDialogOpen, handleOpen, handleClose } = useDialog();
  const shareCreditNotesMutation = useShareCreditNotes();

  const handleShare = async () => {
    handleClose();

    const creditNoteEntries = [{ creditLedgerEntryId }];

    await runMutation(
      shareCreditNotesMutation,
      creditNoteEntries,
      {
        pending: 'Sending credit note email...',
        success: 'Credit note email sent!',
        error: 'Failed to send credit note email',
      },
    );
  };

  return (
    <>
      {React.cloneElement(children, {
        onClick: (event: React.MouseEvent) => {
          children.props.onClick?.(event);
          if (!event.defaultPrevented) {
            handleOpen();
          }
        },
      })}
      <VyDialog
        open={isDialogOpen}
        onClose={handleClose}
        cancelButtonText="Cancel"
        ctaText={
          <Stack
            direction="row"
            sx={{
              alignItems: "center",
              gap: 1
            }}>
            <VyIcon icon="Send" />
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: "15px",
                lineHeight: "26px"
              }}>
              Share
            </Typography>
          </Stack>
        }
        ctaDisabled={isEmptyArray(contactEmails)}
        onSubmit={handleShare}
        label="Share Credit Note"
        data-vy="credit-note-share-email-dialog"
      >
        <Stack direction="column" sx={{
          gap: 2
        }}>
          <Typography variant="body1" sx={{
            color: "text.primary"
          }}>
            <strong>{customerName}</strong> will receive the credit note via email.
          </Typography>
          <Stack direction="column" sx={{
            gap: 0.5
          }}>
            <Typography variant="subtitle2" sx={{
              color: "text.secondary"
            }}>
              Recipients
            </Typography>
            {contactEmails.map((email) => (
              <Typography key={email} variant="body2" data-vy="credit-note-share-email-dialog-recipient">
                {email}
              </Typography>
            ))}
          </Stack>
        </Stack>
      </VyDialog>
    </>
  );
};
